import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { PublicPageLayout } from '../components/PublicPageLayout';
import { api } from '../services/api';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const InfoCard = styled.div`
  background: var(--bg-card);
  border-radius: 18px;
  border: 1px solid rgba(0, 212, 170, 0.12);
  padding: 22px;
  display: flex;
  gap: 16px;
  align-items: flex-start;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.2);
`;

const InfoTitle = styled.h3`
  font-size: 1rem;
  margin-bottom: 6px;
`;

const InfoText = styled.p`
  color: var(--text-muted);
  font-size: 0.92rem;
`;

const FormCard = styled.div`
  background: var(--bg-card);
  border-radius: 18px;
  border: 1px solid rgba(0, 212, 170, 0.12);
  padding: 28px;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.2);
`;

const Form = styled.form`
  display: grid;
  gap: 14px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: #cbd5f5;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #1e293b;
  background: #0f172a;
  color: #f8fafc;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 140px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #1e293b;
  background: #0f172a;
  color: #f8fafc;
  resize: vertical;
`;

const SubmitButton = styled.button`
  background: var(--gradient-primary);
  color: #0a0e17;
  border: none;
  padding: 14px 20px;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
`;

const Status = styled.p`
  color: var(--text-muted);
`;

export const ContactPage: React.FC = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [contactSection, setContactSection] = useState<any | null>(null);

  useEffect(() => {
    const loadContactSection = async () => {
      try {
        const response = await api.get('/site-content');
        const sections = response.data?.data || [];
        setContactSection(sections.find((section: any) => section.key === 'contact_page') || null);
      } catch (error) {
        console.error('Erro ao carregar conteudo de contato:', error);
      }
    };

    loadContactSection();
  }, []);

  const contactCards = useMemo(() => {
    if (contactSection) {
      return contactSection.items || [];
    }

    return [
      { title: 'Email', description: 'suporte@pontofiel.com.br', subtitle: 'comercial@pontofiel.com.br', icon: 'mail' },
      { title: 'Telefone', description: '(11) 4000-1234', subtitle: 'WhatsApp (11) 98888-0000', icon: 'phone' },
      { title: 'Endereco', description: 'Av. Paulista, 1000 - Sao Paulo/SP', subtitle: 'CEP 01310-100', icon: 'map-pin' },
      { title: 'Horario', description: 'Segunda a sexta, 9h - 18h', subtitle: 'Sabado, 9h - 13h', icon: 'clock' },
    ];
  }, [contactSection]);

  const renderIcon = (icon?: string) => {
    switch (icon) {
      case 'mail':
        return <Mail size={22} />;
      case 'phone':
        return <Phone size={22} />;
      case 'map-pin':
        return <MapPin size={22} />;
      case 'clock':
        return <Clock size={22} />;
      default:
        return <Mail size={22} />;
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setStatus('sending');
    try {
      await api.post('/contact', form);
      setStatus('sent');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <PublicPageLayout
      title={contactSection?.title || 'Fale com a gente'}
      subtitle={contactSection?.subtitle || 'Suporte real, atendimento rapido e orientacao completa.'}
      eyebrow="Contato"
    >
      <Grid>
        {contactCards.map((card: any, index: number) => (
          <InfoCard key={card.id || index}>
            {renderIcon(card.icon)}
            <div>
              <InfoTitle>{card.title}</InfoTitle>
              {card.description ? <InfoText>{card.description}</InfoText> : null}
              {card.subtitle ? <InfoText>{card.subtitle}</InfoText> : null}
            </div>
          </InfoCard>
        ))}
      </Grid>

      <FormCard>
        <Form onSubmit={handleSubmit}>
          <Row>
            <div>
              <Label>Nome</Label>
              <Input
                value={form.name}
                onChange={event => setForm({ ...form, name: event.target.value })}
                placeholder="Seu nome"
                required
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={event => setForm({ ...form, email: event.target.value })}
                placeholder="email@empresa.com"
                required
              />
            </div>
          </Row>
          <div>
            <Label>Assunto</Label>
            <Input
              value={form.subject}
              onChange={event => setForm({ ...form, subject: event.target.value })}
              placeholder="Preciso de ajuda com..."
              required
            />
          </div>
          <div>
            <Label>Mensagem</Label>
            <TextArea
              value={form.message}
              onChange={event => setForm({ ...form, message: event.target.value })}
              placeholder="Conte como podemos ajudar."
              required
            />
          </div>
          <SubmitButton type="submit" disabled={status === 'sending'}>
            {status === 'sending' ? 'Enviando...' : 'Enviar mensagem'}
          </SubmitButton>
          {status === 'sent' && <Status>Mensagem enviada com sucesso.</Status>}
          {status === 'error' && <Status>Falha ao enviar. Tente novamente.</Status>}
        </Form>
      </FormCard>
    </PublicPageLayout>
  );
};
